import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, CreditCard, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const WOOD_STAINS = {
  natural: { name: "Natural Plywood", hex: "#E3C28D" },
  antique_pine: { name: "Antique Pine", hex: "#8B5A2B" },
  medium_oak: { name: "Medium Oak", hex: "#A0785A" },
  dark_walnut: { name: "Dark Walnut", hex: "#4A3728" },
  mahogany: { name: "Mahogany", hex: "#662A2C" },
  driftwood: { name: "Driftwood", hex: "#78736E" },
  ebony: { name: "Black Ebony", hex: "#2D2D2D" },
  jacobean: { name: "Jacobean", hex: "#3D2B1F" },
};

const SHAPE_NAMES = {
  rectangular: "Rectangular",
  corner: "Corner (L-Shape)",
  hexagonal: "Hexagonal",
};

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, cartTotal, removeFromCart, loading } = useCart();

  if (!user) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#F4F1DE]/20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
            Please login to view your cart
          </h2>
          <Link to="/login">
            <Button className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
              Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#F4F1DE]/20 mx-auto mb-4" />
          <h2 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
            Your cart is empty
          </h2>
          <p className="text-[#F4F1DE]/60 mb-6">
            Start designing your perfect reptile enclosure
          </p>
          <Link to="/configurator" data-testid="cart-empty-cta">
            <Button className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
              Build Your Enclosure
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleCheckout = () => {
    navigate("/checkout");
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  return (
    <div className="min-h-screen pt-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-8">
          Your Cart
        </h1>

        <div className="space-y-4 mb-8">
          {cart.map((item) => (
            <div
              key={item.id}
              className="bg-[#121212] border border-white/10 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
              data-testid={`cart-item-${item.id}`}
            >
              <div className="flex-1">
                {item.type === "stacking" ? (
                  // Stacking item display
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
                        Stacking Unit ({item.config.stack_count}x)
                      </h3>
                      <span
                        className="w-6 h-6 rounded-full border-2 border-white/20"
                        style={{ backgroundColor: WOOD_STAINS[item.config.stain_color]?.hex }}
                      />
                    </div>
                    <p className="text-[#F4F1DE]/60 text-sm">
                      {item.config.size_name} × {item.config.stack_count} units
                    </p>
                    <p className="text-[#F4F1DE]/40 text-sm">
                      Total Height: {item.config.total_height}ft • {item.config.glass_thickness}mm Glass • {WOOD_STAINS[item.config.stain_color]?.name}
                    </p>
                  </>
                ) : (
                  // Regular enclosure item display
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE]">
                        {SHAPE_NAMES[item.config.shape]} Enclosure
                      </h3>
                      <span
                        className="w-6 h-6 rounded-full border-2 border-white/20"
                        style={{ backgroundColor: WOOD_STAINS[item.config.stain_color]?.hex }}
                      />
                    </div>
                    <p className="text-[#F4F1DE]/60 text-sm">
                      {item.config.width}ft × {item.config.height}ft × {item.config.depth}ft
                    </p>
                    <p className="text-[#F4F1DE]/40 text-sm">
                      {item.config.glass_thickness}mm Glass • {WOOD_STAINS[item.config.stain_color]?.name}
                    </p>
                  </>
                )}
              </div>
              
              <div className="flex items-center gap-6">
                <p className="text-xl font-bold text-[#E07A5F]">
                  £{item.price.toFixed(2)}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveItem(item.id)}
                  className="text-[#F4F1DE]/60 hover:text-red-500"
                  data-testid={`remove-item-${item.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-lg font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-4">
            Order Summary
          </h3>
          <div className="space-y-2 mb-6">
            <div className="flex justify-between text-[#F4F1DE]/60">
              <span>Subtotal ({cart.length} item{cart.length > 1 ? 's' : ''})</span>
              <span>£{cartTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[#F4F1DE]/60">
              <span>Delivery</span>
              <span>TBC</span>
            </div>
            <div className="border-t border-white/10 pt-2 flex justify-between text-xl font-bold text-[#F4F1DE]">
              <span>Total</span>
              <span className="text-[#E07A5F]">£{cartTotal.toFixed(2)}</span>
            </div>
          </div>
          
          <Button
            onClick={handleCheckout}
            className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 font-bold uppercase tracking-widest btn-glow"
            data-testid="checkout-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Proceed to Checkout
          </Button>
          
          <p className="text-xs text-[#F4F1DE]/40 text-center mt-4">
            Secure checkout powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
